// A2F-766: shared configurator body for `/` (signup) and `/configure` (edit).
//
// Extracted from the A2F-764 ProV2Page so both surfaces render the same
// section list + sticky preview without each maintaining its own copy
// of the pill / manualEdits / packs / customRules / tableStyles
// reducers. The asymmetry between the two pages — page-level chrome
// (header, save vs signup block) — is parameterized via a render-prop
// for Section 6 (`renderInstallSection`) while sections 1–5 and the
// right-column live preview stay identical.
//
// Doctype change DOES NOT remount or reset formatting state. v1
// OnboardPage used `key={docType}` on ConfiguratorCore to wipe state
// on doctype switch; v2 only swaps the preview sample. Same logic
// applies on `/configure` per A2F-766 — a returning user changing
// doctype on the editor is overwhelmingly previewing, not asking us
// to throw away their saved configuration. Called out in the PR
// description as an intentional behavior change vs v1 `/configure`.
//
// Tags pass-through: legacy profiles saved through the pre-redesign
// configurator may carry tag keys. Both surfaces plumb tags through
// untouched (defaults to []) so saving from either page never
// silently erases data the user can't see.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Telemetry } from '../../lib/telemetry'
import type { DocType } from '../../lib/telemetry-types'
import {
  applyPills,
  formattersFromState,
  isCardChanged,
  tableStylesChanged,
  type CustomRuleChip,
  type FormattingPill,
} from '../../lib/onboard-v2-formatting'
import {
  BASE_FORMATTING,
  DEFAULT_TABLE_STYLES,
  FIELD_GROUPS,
  type FormattingFields,
  type FormattingValue,
} from '../../data/onboard-v2-fields'
import { buildProfile } from '../../lib/profile-assembly'
import type { Formatters, Profile, Rule, TableStylePreset } from '../../types'
import { PACKS } from '../../data/packs'
import { ALL_RULES_BY_ID } from '../../data/tag-rules'

import { CustomRulesSection } from '../onboard/v2/CustomRulesSection'
import { type PacksState } from '../onboard/v2/PacksList'

import { SectionPacks } from './SectionPacks'
import { SectionTableStyles } from './SectionTableStyles'
import { ProV2Hero } from './ProV2Hero'
import { SectionCard } from './SectionCard'
import { SectionDocType, DOC_TYPE_LABELS } from './SectionDocType'
import { SectionFormatting, isAnyFormattingChanged } from './SectionFormatting'
import { FauxWordPreview } from './FauxWordPreview'
import {
  useFocusedSection,
  type FormattingSubTab,
  type SectionId,
} from './useFocusedSection'
import { PROV2_SAMPLES } from '../../data/prov2-samples'

const DOMAIN = 'biotech_ectd'
const DEFAULT_DOCTYPE: DocType = 'fda'

export const DEFAULT_PACKS: PacksState = {
  indSectionHeadings: true,
  coverLetterValidation: false,
  fdaTerminology: false,
  ectdFormatting: false,
  ndaReviewersGuide: false,
}

/** Pack toggles → registered pack ids that ride along in profile.packs. */
export function enabledPackIds(packs: PacksState): string[] {
  const ids: string[] = []
  if (packs.coverLetterValidation) ids.push('ind_v0')
  // A2F-796: FDA Writing Standards pack. The fdaTerminology toggle has
  // existed in PacksState since the redesign was scoped; the
  // configurator UI may not surface it yet, but rounding-tripping the
  // id here keeps profiles that enable the pack from My Chrona safe
  // across a /configure save.
  if (packs.fdaTerminology) ids.push('fda_writing_standards_v0')
  // A2F-801: eCTD Document Formatting pack. Same shape as the
  // fdaTerminology line above — the toggle now maps to a real
  // registered pack with rule content, so the id rides along in
  // profile.packs and the merged rules pull in via mergeProfileRules.
  if (packs.ectdFormatting) ids.push('ectd_formatting_v0')
  // A2F-803: NDA Reviewers Guide pack. Same shape — toggle round-trips
  // through profile.packs; pack content (currently one rule consuming
  // the document-target executor primitive) merges via mergeProfileRules.
  if (packs.ndaReviewersGuide) ids.push('nda_reviewers_guide_v0')
  return ids
}

/**
 * Expand enabled packs into their rule definitions and dedup-merge with
 * the user's active custom-rule chips. Pack rules go first so a pack
 * copy wins over a same-id custom chip (preserves pack tier/icon).
 *
 * A2F-780: signup path must include this expansion so style_profiles
 * carries the pack's rules from the very first write. Word's My Chrona
 * taskpane renders `profile.rules` directly — it does not expand packs
 * — so a profile with `packs:["ind_v0"]` but an empty `rules` shows no
 * Cover Letter Validation rules on first open, until the user toggles
 * the pack off/on from /configure and triggers a full save through this
 * same merge.
 */
export function mergeProfileRules(
  packs: PacksState,
  customRules: CustomRuleChip[],
): Rule[] {
  const seen = new Set<string>()
  const out: Rule[] = []
  for (const packId of enabledPackIds(packs)) {
    const pack = PACKS[packId]
    if (!pack) continue
    for (const rid of pack.ruleIds) {
      if (seen.has(rid)) continue
      const rule = ALL_RULES_BY_ID[rid]
      if (rule) {
        seen.add(rid)
        out.push(rule)
      }
    }
  }
  for (const chip of customRules) {
    if (chip.status !== 'active' || !chip.rule) continue
    if (seen.has(chip.rule.id)) continue
    seen.add(chip.rule.id)
    out.push(chip.rule)
  }
  return out
}

type SectionOpenState = Record<SectionId, boolean>

const CAPTIONS_GROUP = FIELD_GROUPS.find((g) => g.id === 'captions')

export interface ConfiguratorV2InitialState {
  manualEdits: FormattingFields
  packs: PacksState
  customRules: CustomRuleChip[]
  tableStyles: TableStylePreset[]
  tags: string[]
  // A2F-796: opaque pass-through from profile.disabledPacks. The
  // configurator doesn't surface a UI for it — set on the My Chrona
  // taskpane — but it must round-trip through hydration and save or a
  // /configure save would silently re-enable a pack the user disabled.
  disabledPacks?: string[]
}

export interface ConfiguratorV2State {
  manualEdits: FormattingFields
  packs: PacksState
  customRules: CustomRuleChip[]
  tableStyles: TableStylePreset[]
  pills: FormattingPill[]
  tags: string[]
  disabledPacks: string[]
}

export interface ConfiguratorV2SummaryCounts {
  ribbonButtons: number
  packs: number
  customRules: number
  tablePresets: number
}

export interface ConfiguratorV2InstallRenderArgs {
  summary: ConfiguratorV2SummaryCounts
  outgoingProfile: Profile
  isOpen: boolean
  onToggle: () => void
  edited: boolean
  onFocus: (id: SectionId) => void
}

interface ConfiguratorV2BodyProps {
  mode: 'signup' | 'edit'
  /** When omitted, body initializes to fresh defaults (signup baseline). */
  initialState?: ConfiguratorV2InitialState
  /** Streams state to the parent on every change (used by /configure for dirty tracking). */
  onStateChange?: (state: ConfiguratorV2State) => void
  /** Page-level render of Section 6 — signup block on `/`, save block on `/configure`. */
  renderInstallSection: (args: ConfiguratorV2InstallRenderArgs) => ReactNode
}

export function ConfiguratorV2Body({
  mode,
  initialState,
  onStateChange,
  renderInstallSection,
}: ConfiguratorV2BodyProps) {
  // ── State tree ───────────────────────────────────────────────────
  const [docType, setDocType] = useState<DocType>(DEFAULT_DOCTYPE)
  const [pills, setPills] = useState<FormattingPill[]>([])
  const [manualEdits, setManualEdits] = useState<FormattingFields>(
    () => initialState?.manualEdits ?? {},
  )
  const [customRules, setCustomRules] = useState<CustomRuleChip[]>(
    () => initialState?.customRules ?? [],
  )
  const [packs, setPacks] = useState<PacksState>(
    () => initialState?.packs ?? DEFAULT_PACKS,
  )
  const [tableStyles, setTableStyles] = useState<TableStylePreset[]>(
    () => initialState?.tableStyles ?? DEFAULT_TABLE_STYLES,
  )
  const [activeStyleIdx, setActiveStyleIdx] = useState(0)
  // Tags ride through unchanged. No UI surfaces them; parent provides
  // the initial slice and the outgoing profile carries it back.
  const tags = useMemo(() => initialState?.tags ?? [], [initialState])
  // A2F-796: disabledPacks rides through the same way as tags — owned
  // by the My Chrona surface, opaque to /configure, but must survive
  // the save round-trip because the backend wholesale-replaces the
  // JSONB blob and an omitted field becomes an erased field.
  const disabledPacks = useMemo(
    () => initialState?.disabledPacks ?? [],
    [initialState],
  )

  // Install always starts expanded — on signup it's the conversion
  // moment; on edit it's where the Save CTA lives, so collapsing it
  // would hide the primary action behind an accordion the user has
  // to discover. Doctype starts open on signup (the first thing a
  // new user picks) and closed on edit (returning users already have
  // a saved doctype, no need to re-prompt).
  const [openSections, setOpenSections] = useState<SectionOpenState>(() => ({
    doctype: mode === 'signup',
    formatting: false,
    tables: false,
    packs: false,
    rules: false,
    install: true,
  }))
  const [formattingSubTab, setFormattingSubTab] = useState<FormattingSubTab>('headings')
  const { section: focusedSection, ribbonId, setFocus } = useFocusedSection()

  // ── Derived ──────────────────────────────────────────────────────
  const formattingState = useMemo<FormattingFields>(
    () => ({ ...applyPills(BASE_FORMATTING, pills), ...manualEdits }),
    [pills, manualEdits],
  )

  const formatters = useMemo<Formatters | undefined>(() => {
    const fromFlat = formattersFromState(formattingState)
    const tablesEdited = tableStylesChanged(tableStyles, DEFAULT_TABLE_STYLES)
    if (tablesEdited) return { ...(fromFlat ?? {}), tableStyles }
    return fromFlat
  }, [formattingState, tableStyles])

  const outgoingProfile = useMemo<Profile>(() => {
    return buildProfile({
      domain: DOMAIN,
      tags,
      packs: enabledPackIds(packs),
      rules: mergeProfileRules(packs, customRules),
      formatters,
      // disabledPacks intentionally omitted: outgoingProfile feeds
      // OnboardPage signup (a fresh-insert path with no existing
      // value to preserve) and the configurator save (which relies
      // on backend partial-merge to keep My Chrona's disabledPacks
      // — see ConfiguratorPage.handleSave). Either path is safe
      // without the field; sending it would re-introduce the
      // cross-tab race A2F-796 closed in commit 33e6dfb.
    })
  }, [packs, customRules, formatters, tags])

  // Stream state upstream so /configure can drive the dirty bit and
  // assemble the save body. Memoized stateBag means the effect only
  // re-fires on real state changes, not callback-identity churn.
  const stateBag = useMemo<ConfiguratorV2State>(
    () => ({ manualEdits, packs, customRules, tableStyles, pills, tags, disabledPacks }),
    [manualEdits, packs, customRules, tableStyles, pills, tags, disabledPacks],
  )
  useEffect(() => {
    if (onStateChange) onStateChange(stateBag)
    // onStateChange intentionally omitted — parents are expected to
    // useCallback it; this prevents identity churn from re-firing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateBag])

  const summary = useMemo<ConfiguratorV2SummaryCounts>(() => {
    const enabledPackCount = Object.values(packs).filter(Boolean).length
    return {
      ribbonButtons: 5 + (packs.indSectionHeadings ? 1 : 0),
      packs: enabledPackCount,
      customRules: customRules.filter((c) => c.status === 'active').length,
      tablePresets: tableStyles.length,
    }
  }, [packs, customRules, tableStyles])

  const formattingEdited = useMemo(
    () => isAnyFormattingChanged(formattingState),
    [formattingState],
  )
  const captionsEditedKeys = useMemo(() => {
    if (!CAPTIONS_GROUP) return false
    return CAPTIONS_GROUP.cards.some((c) => isCardChanged(formattingState, c.keys))
  }, [formattingState])

  const edited: Record<SectionId, boolean> = {
    doctype: docType !== DEFAULT_DOCTYPE,
    formatting: formattingEdited || captionsEditedKeys,
    tables: tableStylesChanged(tableStyles, DEFAULT_TABLE_STYLES),
    packs:
      packs.coverLetterValidation ||
      packs.fdaTerminology ||
      packs.ectdFormatting ||
      packs.ndaReviewersGuide,
    rules: customRules.some((c) => c.status === 'active' || c.status === 'coming_soon'),
    install: false,
  }

  // ── Mutators ─────────────────────────────────────────────────────
  const handleAddPill = useCallback(
    (pill: FormattingPill) => {
      setPills((prev) => [...prev, pill])
      setManualEdits((prev) => {
        let next: FormattingFields | null = null
        for (const key of Object.keys(pill.fieldChanges)) {
          if (key in prev) {
            if (!next) next = { ...prev }
            delete next[key]
          }
        }
        return next ?? prev
      })
      if (pill.tableStylePatch) {
        setTableStyles((prev) => {
          if (prev.length === 0) return prev
          const idx = Math.min(activeStyleIdx, prev.length - 1)
          const next = prev.slice()
          next[idx] = { ...next[idx], ...pill.tableStylePatch }
          return next
        })
      }
    },
    [activeStyleIdx],
  )

  const handleEditField = useCallback(
    (key: string, value: FormattingValue) => {
      setManualEdits((prev) => {
        if (value === BASE_FORMATTING[key]) {
          if (!(key in prev)) return prev
          const next = { ...prev }
          delete next[key]
          return next
        }
        if (prev[key] === value) return prev
        return { ...prev, [key]: value }
      })
      Telemetry.onboardingFormatFieldChanged({ path: key })
    },
    [],
  )

  const handleAddRule = useCallback((chip: CustomRuleChip) => {
    setCustomRules((prev) => [...prev, chip])
  }, [])

  const handleRemoveRule = useCallback((id: string) => {
    setCustomRules((prev) => prev.filter((c) => c.id !== id))
  }, [])

  const handleTogglePack = useCallback((key: keyof PacksState) => {
    if (key === 'indSectionHeadings') return
    setPacks((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      // A2F-797: derive from the registry instead of hard-coding a
      // per-toggle constant. The previous form `next.coverLetterValidation ? 4 : 0`
      // reported 4 for ind_v0 (wrong: pack has 5 rules post-A2F-797) and
      // 0 for any other toggle (e.g. fdaTerminology toggling on emitted
      // rule_count_after:0 despite enabling 4 rules). enabledPackIds is
      // the canonical PacksState → pack-id mapping, and PACKS[id].ruleIds
      // is the source of truth for content.
      const enabledIds = enabledPackIds(next)
      const ruleIds = new Set<string>()
      for (const packId of enabledIds) {
        const pack = PACKS[packId]
        if (!pack) continue
        for (const rid of pack.ruleIds) ruleIds.add(rid)
      }
      Telemetry.onboardingPackToggled({
        pack_id: key,
        enabled: next[key],
        rule_count_after: ruleIds.size,
      })
      return next
    })
  }, [])

  const isFirstDocTypeChangeRef = useRef(true)
  const handleDocTypeChange = useCallback(
    (next: DocType) => {
      if (next === docType) return
      Telemetry.onboardingDoctypeSelected({
        doctype: next,
        is_default_change: isFirstDocTypeChangeRef.current,
      })
      isFirstDocTypeChangeRef.current = false
      setDocType(next)
    },
    [docType],
  )

  const toggleSection = useCallback((id: SectionId) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const handleSubTabChange = useCallback(
    (next: FormattingSubTab) => {
      setFormattingSubTab(next)
      setFocus('formatting', next)
    },
    [setFocus],
  )

  // ── Render ───────────────────────────────────────────────────────
  const sample = PROV2_SAMPLES[docType]
  const packsActive = {
    coverLetter: packs.coverLetterValidation,
    indSectionHeadings: packs.indSectionHeadings,
  }
  const activeTableStyle =
    tableStyles[Math.min(activeStyleIdx, Math.max(0, tableStyles.length - 1))] ??
    DEFAULT_TABLE_STYLES[0]

  return (
    <main className="mx-auto max-w-[1440px] px-6 py-8 grid gap-6 grid-cols-1 xl:[grid-template-columns:minmax(0,1.2fr)_minmax(0,1fr)]">
      {/* LEFT: scrolling configurator */}
      <div className="min-w-0">
        <div className="mx-auto xl:mx-0 w-full max-w-[700px]">
          {mode === 'signup' ? <ProV2Hero /> : null}

          <div className="space-y-3">
            <SectionCard
              id="doctype"
              number={1}
              title="What kind of documents do you work on?"
              subtitle="Pre-selects sensible defaults. Change anything later."
              isOpen={openSections.doctype}
              edited={edited.doctype}
              onToggle={() => toggleSection('doctype')}
              onFocus={setFocus}
              statusOverride={
                <span className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 bg-brand-orange/10 text-brand-orange">
                  {DOC_TYPE_LABELS[docType]}
                </span>
              }
            >
              <SectionDocType value={docType} onChange={handleDocTypeChange} />
            </SectionCard>

            <SectionCard
              id="formatting"
              number={2}
              title="Configure your formatting"
              subtitle="Headings · body text · captions · lists. Your house style, codified."
              isOpen={openSections.formatting}
              edited={edited.formatting}
              onToggle={() => toggleSection('formatting')}
              onFocus={(id) => setFocus(id, formattingSubTab)}
            >
              <SectionFormatting
                state={formattingState}
                subTab={formattingSubTab}
                onSubTabChange={handleSubTabChange}
                onEditField={handleEditField}
              />
            </SectionCard>

            <SectionCard
              id="tables"
              number={3}
              title="Table styles"
              subtitle="One preset to start. Add more — each becomes a row in the Apply Table Style task pane."
              isOpen={openSections.tables}
              edited={edited.tables}
              onToggle={() => toggleSection('tables')}
              onFocus={setFocus}
            >
              <SectionTableStyles
                presets={tableStyles}
                onChange={setTableStyles}
                activeIdx={Math.min(
                  activeStyleIdx,
                  Math.max(0, tableStyles.length - 1),
                )}
                onActiveChange={setActiveStyleIdx}
              />
            </SectionCard>

            <SectionCard
              id="packs"
              number={4}
              title="Regulatory packs"
              subtitle="Pre-built rule packs maintained by Chrona. IND Section Headings free; others Pro."
              isOpen={openSections.packs}
              edited={edited.packs}
              onToggle={() => toggleSection('packs')}
              onFocus={setFocus}
            >
              <SectionPacks packs={packs} onTogglePack={handleTogglePack} />
            </SectionCard>

            <SectionCard
              id="rules"
              number={5}
              title="Custom rules"
              subtitle="Plain English in. Each becomes a one-click check in the My Chrona task pane."
              isOpen={openSections.rules}
              edited={edited.rules}
              onToggle={() => toggleSection('rules')}
              onFocus={setFocus}
            >
              <CustomRulesSection
                customRules={customRules}
                onAddPill={handleAddPill}
                onAddRule={handleAddRule}
                onRemoveRule={handleRemoveRule}
                domain={DOMAIN}
              />
            </SectionCard>

            {renderInstallSection({
              summary,
              outgoingProfile,
              isOpen: openSections.install,
              onToggle: () => toggleSection('install'),
              edited: edited.install,
              onFocus: setFocus,
            })}
          </div>
        </div>
      </div>

      {/* RIGHT: sticky live preview */}
      <aside className="min-w-0 xl:sticky xl:top-[64px] xl:self-start xl:h-[calc(100vh-88px)]">
        <FauxWordPreview
          sample={sample}
          state={formattingState}
          tableStyle={activeTableStyle}
          packsActive={packsActive}
          pulseId={ribbonId}
          focusedSection={focusedSection}
        />
      </aside>
    </main>
  )
}
